import { t } from 'i18next';
import * as Alert from '../../components/Alert/Alert';
import { getState, redux } from '../../reduxService';
import { IProjectResultResponse } from '../MACRA_1/api/IApiResponse';
import { roundToTwo } from '../MACRA_1/components/InputForm/Geometry/utils';
import { IProjectMacra1 } from '../MACRA_1/store/project/projectSlice';
import { ISlopedGabionResults, ISteppedGabionResults, IVerticalDropsResults } from '../MACRA_2/api/IApiResponse';
import { IChannelGeometryVerticalDropForm } from '../MACRA_2/components/InputForm/ChannelGeometry/IChannelGeometry';
import { IHydraulicDataSlopedWeirForm } from '../MACRA_2/components/InputForm/HydraulicData/IHydraulicData';
import { IStillingBasinVerticalDropForm } from '../MACRA_2/components/InputForm/StillingBasin/IStillingBasin';
import { IWeirGeometrySlopedWeirForm, IWeirGeometrySteppedWeirForm, IWeirGeometryVerticalDropForm } from '../MACRA_2/components/InputForm/WeirGeometry/IWeirGeometry';
import { IWingsGeometrySlopedWeirForm, IWingsGeometrySteppedWeirForm, IWingsGeometryVerticalDropForm } from '../MACRA_2/components/InputForm/WingsGeometry/IWingsGeometry';
import { EVerticalDropsTypes } from '../MACRA_2/enum/general';
import { IProjectMacra2 } from '../MACRA_2/store/project/projectSlice';
import { getActionByProjectType } from '../actionMap';
import { IMacra2TemplateRequest, Info } from '../api/IApiRequest';
import { IProjectMacra1Response, IProjectMacra2Response } from '../api/IApiResponse';
import * as BeMaccaferriStoreHelper from '../api/store/BeMaccaferriStoreHelper';
import { EProjectType } from '../enum/project';
import { isConcrete } from '../enum/Material';
import { IProjectInfoForm } from '../pages/ProjectInfo/IProjectInfo';
import { getProjectInfo } from '../store';
import { setProjectInfo } from '../store/projectInfo/projectInfoSlice';

export const saveFile = (filename: string, fileContent: string) => {
    let bb = new Blob([fileContent], { type: 'text/plain' });
    let a = document.createElement('a');
    a.download = filename + '.mcr';
    a.href = window.URL.createObjectURL(bb);
    a.click();
};

export const removeItem = <T extends any>(arr: Array<T>, value: T): Array<T> => {
    const index = arr.indexOf(value);
    if (index > -1) {
        arr.splice(index, 1);
    }
    return arr;
};

export const isLocalEnv = () => (process.env.ENVIRONMENT as string) === 'local';

export const isDevelopmentEnv = () => (process.env.ENVIRONMENT as string) === 'dev';

export const isProductionEnv = () => (process.env.ENVIRONMENT as string) === 'prod';

export const saveProject = async (pInfo: IProjectInfoForm, hideAlert?: boolean): Promise<IProjectMacra1Response | IProjectMacra2Response | undefined> => {
    let responseProject: any;
    const { setProject } = getActionByProjectType(pInfo.type);
    const state = getState();
    const project = pInfo.type === EProjectType.Macra1 ? state.macra1.project.data : state.macra2.project.data;
    if (!!pInfo.id) {
        let hasNewName = pInfo.name.toLowerCase() !== getProjectInfo().data?.name.toLowerCase();
        if (pInfo.type === EProjectType.Macra1) {
            responseProject = !hasNewName
                ? (await BeMaccaferriStoreHelper.updateProjectCBPAsync(project as IProjectMacra1, pInfo)).data
                : (await BeMaccaferriStoreHelper.addProjectCBPAsync(project as IProjectMacra1, pInfo)).data;
            redux.dispatch(
                setProject({
                    geometry: responseProject.sectors,
                    hydraulicData: responseProject.hydraulicData,
                }),
            );
        } else {
            responseProject = !hasNewName
                ? (await BeMaccaferriStoreHelper.updateProjectGCSAsync(project as IProjectMacra2, pInfo)).data
                : (await BeMaccaferriStoreHelper.addProjectGCSAsync(project as IProjectMacra2, pInfo)).data;
            redux.dispatch(setProject(getProjectResponseForRedux(responseProject) as any));
        }
        redux.dispatch(
            setProjectInfo({
                ...pInfo,
                id: responseProject.id,
                name: responseProject.name,
            }),
        );
    } else {
        if (pInfo.type === EProjectType.Macra1) {
            responseProject = (await BeMaccaferriStoreHelper.addProjectCBPAsync(project as IProjectMacra1, pInfo)).data;
            redux.dispatch(
                setProject({
                    geometry: responseProject.sectors as any,
                    hydraulicData: responseProject.hydraulicData as any,
                }),
            );
        } else {
            responseProject = (await BeMaccaferriStoreHelper.addProjectGCSAsync(project as IProjectMacra2, pInfo)).data;
            redux.dispatch(setProject(getProjectResponseForRedux(responseProject) as any));
        }
        redux.dispatch(
            setProjectInfo({
                ...pInfo,
                id: responseProject.id,
                name: responseProject.name,
            }),
        );
    }
    if (!!responseProject) {
        if (!hideAlert) Alert.success(t('header_projects_saved'));
        return responseProject;
    }
};

export const projectMacra2ToTemplate = (project: IProjectMacra2, infos: Info[]) => {
    let payload: IMacra2TemplateRequest = {
        infos: infos,
        geometry: {
            riverGeometryBankTopElevation: project.geometry!.bankTopElevation,
            id: project.geometry?.id,
            materialId: project.geometry?.materialId,
            riverGeometryChannelWidth: project.geometry!.channelWidth,
            riverGeometrySlopeOfLeftBank: (<IChannelGeometryVerticalDropForm>project.geometry).slopeOfLeftBank,
            riverGeometrySlopeOfRightBank: (<IChannelGeometryVerticalDropForm>project.geometry).slopeOfRightBank,

            weirGeometryStepNumber: (<IWeirGeometrySteppedWeirForm>project.weirGeometry).stepNumber,
            weirGeometryGabbionWidth: (<IWeirGeometrySteppedWeirForm>project.weirGeometry).gabionWidth,
            weirGeometryStepHeight: (<IWeirGeometrySteppedWeirForm>project.weirGeometry).stepHeight,
            weirGeometryFoundationLayers: (<IWeirGeometrySteppedWeirForm>project.weirGeometry).foundationLayers,
            weirGeometryTopLenght: (<IWeirGeometrySlopedWeirForm>project.weirGeometry).crestLength,
            weirGeometryStepRatio: (<IWeirGeometrySteppedWeirForm | IWeirGeometrySlopedWeirForm>project.weirGeometry).stepRatio,
            weirGeometryLiningThickness: (<IWeirGeometrySlopedWeirForm>project.weirGeometry).liningThickness,
            weirGeometryBottomLength: (<IWeirGeometrySlopedWeirForm>project.weirGeometry).bottomLength,
            weirGeometryHeight: (<IWeirGeometrySlopedWeirForm>project.weirGeometry).height,
            weirGeometryFoundation: (<IWeirGeometryVerticalDropForm>project.weirGeometry).foundation,
            weirGeometryTableLayer: (<IWeirGeometryVerticalDropForm>project.weirGeometry).layer,

            wingsGeometryWidth: project.wingsGeometry!.width,
            wingsGeometryHeight: (<IWingsGeometryVerticalDropForm | IWingsGeometrySlopedWeirForm>project.wingsGeometry).height,
            wingsGeometryOffset: (<IWingsGeometryVerticalDropForm | IWingsGeometrySteppedWeirForm>project.wingsGeometry).offset,
            wingsGeometrySlopeAngle: (<IWingsGeometryVerticalDropForm>project.wingsGeometry).slopeAngle,

            stillingBasinIsCounterweir: project.stillingBasin!.isCounterweir,
            stillingBasinCounterweirElevation: project.stillingBasin!.counterweirElevation,
            stillingBasinPoolLength: project.stillingBasin!.poolLength,
            stillingBasinIsStillinBasin: project.stillingBasin!.isStillingBasin,
            stillingBasinWidth: project.stillingBasin!.width,
            stillingBasinThikness: project.stillingBasin!.thickness,
            stillingBasinElevation: project.stillingBasin!.stillingBasinElevation,

            material: project.stillingBasin!.material,
        },
        soilData: project.soilData,
        hydraulicData: project.hydraulicData!,
    };

    return payload;
};

export const getProjectResponseForRedux = (resProject: IProjectMacra2Response): IProjectMacra2 => {
    let project: IProjectMacra2 = {
        geometry: {
            bankTopElevation: resProject.geometry.riverGeometryBankTopElevation,
            channelWidth: resProject.geometry.riverGeometryChannelWidth,
            slopeOfLeftBank: resProject.geometry.riverGeometrySlopeOfLeftBank,
            slopeOfRightBank: resProject.geometry.riverGeometrySlopeOfRightBank,
            id: resProject.geometry.id,
            materialId: resProject.geometry.materialId,
        },
        hydraulicData: resProject.hydraulicData,
        weirGeometry: {
            stepNumber: resProject.geometry.weirGeometryStepNumber,
            gabionWidth: resProject.geometry.weirGeometryGabbionWidth,
            stepHeight: resProject.geometry.weirGeometryStepHeight,
            foundationLayers: resProject.geometry.weirGeometryFoundationLayers,
            crestLength: resProject.geometry.weirGeometryTopLenght,
            stepRatio: resProject.geometry.weirGeometryStepRatio,
            liningThickness: resProject.geometry.weirGeometryLiningThickness,
            bottomLength: resProject.geometry.weirGeometryBottomLength,
            height: resProject.geometry.weirGeometryHeight,
            foundation: resProject.geometry.weirGeometryFoundation,
            layer: resProject.geometry.weirGeometryTableLayer,
        },
        wingsGeometry: {
            width: resProject.geometry.wingsGeometryWidth,
            height: resProject.geometry.wingsGeometryHeight,
            offset: resProject.geometry.wingsGeometryOffset,
            slopeAngle: resProject.geometry.wingsGeometrySlopeAngle,
        },
        stillingBasin: {
            isCounterweir: resProject.geometry.stillingBasinIsCounterweir,
            counterweirElevation: resProject.geometry.stillingBasinCounterweirElevation,
            poolLength: resProject.geometry.stillingBasinPoolLength,
            isStillingBasin: resProject.geometry.stillingBasinIsStillinBasin,
            width: resProject.geometry.stillingBasinWidth,
            thickness: resProject.geometry.stillingBasinThikness,
            stillingBasinElevation: resProject.geometry.stillingBasinElevation,
            material: resProject.geometry.material,
        },
        soilData: resProject.soilData,
    };
    return project;
};

export const getVerticalDropsType = (stillingBasin: IStillingBasinVerticalDropForm | undefined): EVerticalDropsTypes => {
    if (!stillingBasin) return EVerticalDropsTypes.NotSet;
    if (!stillingBasin.isCounterweir) return EVerticalDropsTypes.UnlinedVerticalDrop;
    if (stillingBasin.isCounterweir && !stillingBasin.isStillingBasin) return EVerticalDropsTypes.UnlinedPoolAndCountweir;
    if (stillingBasin.isCounterweir && stillingBasin.isStillingBasin && stillingBasin.stillingBasinElevation === 0) return EVerticalDropsTypes.LinedPoolAndCountweirWithoutScoutDepth;
    if (stillingBasin.isCounterweir && stillingBasin.isStillingBasin && stillingBasin.stillingBasinElevation! < 0) return EVerticalDropsTypes.LinedPoolAndCountweirWithNegativeScoutDepth;
    else return EVerticalDropsTypes.NotSet;
};

export const getErrorBreadcrumb = (): IErrorBreadcrumb => {
    let errorMessage = '';
    let type = EErrorBreadcrumb.None;
    let results, project;
    let projectInfo = getState().projectInfo.data;
    if (projectInfo?.type == EProjectType.Macra1) {
        results = getState().macra1.results.data;
        project = getState().macra1.project.data;
    } else {
        results = getState().macra2.results.data;
        project = getState().macra2.project.data;
    }
    if (!!results) {
        let errors = '';
        let isThereShareStressError_Macra1 = (results as IProjectResultResponse)?.materialsResult?.some(x => !isConcrete(x.category) && x.isDangerousShearStress);
        let isThereVelocityUnderLiningError_Macra1 = (results as IProjectResultResponse)?.materialsResult?.some(x => !isConcrete(x.category) && x.isDangerousVelocityUnderLining);
        let materialError = isThereShareStressError_Macra1 || isThereVelocityUnderLiningError_Macra1;

        let isThereShareStressError_Macra2_Stepped =
            (project as IProjectMacra2)?.stillingBasin?.material.physicalData![0].allowShearStress! < (results as ISteppedGabionResults)?.basinDesign?.shearStress!;
        let isThereShareStressError_Macra2_Vertical =
            (results as IVerticalDropsResults)?.hydraulicResult?.shearStressInTheBasin > (project as IProjectMacra2).stillingBasin?.material.physicalData![0].allowShearStress!;
        let isThereShareStressError_Macra2 = isThereShareStressError_Macra2_Vertical || isThereShareStressError_Macra2_Stepped;

        let isThereVelocityUnderLiningError_Macra2_Sloped =
            (project?.hydraulicData as IHydraulicDataSlopedWeirForm)?.soilAdmissibleVelocity < (results as ISlopedGabionResults)?.basinDesign?.residualVelocityUnderGeotextile;
        let isThereVelocityUnderLiningError_Macra2 = isThereVelocityUnderLiningError_Macra2_Sloped;

        let isThereStability_Macra2_Vertical = (results as IVerticalDropsResults)?.staticResult?.stabilityChecksOutput?.isStabilityError;
        let isThereStaticError = isThereStability_Macra2_Vertical;

        let isThereStillingBasinWidthError_Macra2_Vertical = (project as IProjectMacra2).stillingBasin?.poolLength! < (results as IVerticalDropsResults)?.hydraulicResult?.minimumBasinLengthUsace1990;
        let isThereStillingBasinWidthError_Macra2_Stepped = (project as IProjectMacra2)?.stillingBasin?.poolLength! < (results as ISteppedGabionResults)?.basinDesign?.minLengthOfTheStillingPool!;
        let isThereStillingBasinWidthError_Macra2_Sloped = (project as IProjectMacra2)?.stillingBasin?.poolLength! < (results as ISlopedGabionResults)?.basinDesign?.jumpLength!;
        let isThereStillingBasinWidthError_Macra2 = isThereStillingBasinWidthError_Macra2_Vertical || isThereStillingBasinWidthError_Macra2_Stepped || isThereStillingBasinWidthError_Macra2_Sloped;

        let isThereCounterweirElevationError_Macra2_Stepped =
            (project as IProjectMacra2)?.stillingBasin?.counterweirElevation! < (results as ISteppedGabionResults)?.basinDesign?.minCounterweirHeight!;
        let isThereCounterweirElevationError_Macra2_Sloped = (project as IProjectMacra2)?.stillingBasin?.counterweirElevation! < (results as ISlopedGabionResults)?.basinDesign?.minCounterweirHeight!;
        let isThereCounterweirElevationError_Macra2 = isThereCounterweirElevationError_Macra2_Stepped || isThereCounterweirElevationError_Macra2_Sloped;

        let isThereBasinElevation_Macra2_Vertical_Hydraulic =
            getVerticalDropsType((project as IProjectMacra2)?.stillingBasin!) === EVerticalDropsTypes.LinedPoolAndCountweirWithNegativeScoutDepth &&
            (results as IVerticalDropsResults)?.hydraulicResult?.minimumBasinElevation! > Math.abs((project as IProjectMacra2)?.stillingBasin?.stillingBasinElevation!);
        let isThereTypeOfFlowError_Marca2_Stepped_Hydraulic = (results as ISteppedGabionResults)?.hydraulicUpstream?.typeOfFlow === 'Transition flow';
        let isThereCounterweirElevationError_Macra2_Vertical =
            getVerticalDropsType((project as IProjectMacra2)?.stillingBasin!) !== EVerticalDropsTypes.LinedPoolAndCountweirWithNegativeScoutDepth &&
            (results as IVerticalDropsResults)?.hydraulicResult?.minimumCounterweirElevation > (project as IProjectMacra2)?.stillingBasin?.counterweirElevation!;
        let isThereHydraulicError = isThereBasinElevation_Macra2_Vertical_Hydraulic || isThereTypeOfFlowError_Marca2_Stepped_Hydraulic || isThereCounterweirElevationError_Macra2_Vertical;

        let isThereScourDepthError_Macra2_Vertical =
            Math.abs((results as IVerticalDropsResults)?.hydraulicResult?.scourDepth) > ((project as IProjectMacra2).weirGeometry as IWeirGeometryVerticalDropForm)?.foundation;
        let isThereScourDepthError_Macra2 = isThereScourDepthError_Macra2_Vertical;

        let isThereBasinError =
            isThereVelocityUnderLiningError_Macra2 ||
            isThereShareStressError_Macra2 ||
            isThereStillingBasinWidthError_Macra2 ||
            isThereCounterweirElevationError_Macra2 ||
            isThereScourDepthError_Macra2;

        if (materialError) {
            if (isThereShareStressError_Macra1) {
                errors = handleAddErrorType(errors, t('share_stress'));
            }
            if (isThereVelocityUnderLiningError_Macra1) {
                errors = handleAddErrorType(errors, t('velocity_under_lining'));
            }
            type = EErrorBreadcrumb.Macra1_Material;
        } else {
            if (isThereBasinError) {
                if (isThereVelocityUnderLiningError_Macra2) {
                    errors = handleAddErrorType(errors, t('velocity_under_lining'));
                }
                if (isThereShareStressError_Macra2) {
                    errors = handleAddErrorType(errors, t('share_stress'));
                }
                if (isThereStillingBasinWidthError_Macra2) {
                    errors = handleAddErrorType(errors, t('stilling_basin'));
                }
                if (isThereCounterweirElevationError_Macra2) {
                    errors = handleAddErrorType(errors, t('counterweir_elevation'));
                }
                if (isThereScourDepthError_Macra2) {
                    errors = handleAddErrorType(errors, t('scour_depth'));
                }
                type += EErrorBreadcrumb.Macra2_Basin;
            }
            if (isThereHydraulicError) {
                if (isThereBasinElevation_Macra2_Vertical_Hydraulic) {
                    errors = handleAddErrorType(errors, t('stilling_basin'));
                }
                if (isThereCounterweirElevationError_Macra2_Vertical) {
                    errors = handleAddErrorType(errors, t('counterweir_elevation'));
                }
                if (isThereTypeOfFlowError_Marca2_Stepped_Hydraulic) {
                    errors = handleAddErrorType(errors, t('transition_flow'));
                }
                type += EErrorBreadcrumb.Macra2_Hydraulic;
            }
            if (isThereStaticError) {
                if (isThereStability_Macra2_Vertical) {
                    errors = handleAddErrorType(errors, t('stability'));
                }
                type += EErrorBreadcrumb.Macra2_Static;
            }
        }

        errorMessage = t('warning_dynamin', { value: errors }).replace(',', '');
    }
    return {
        errorMessage,
        type,
    };
};

const handleAddErrorType = (errorMessage: string, errorType: string): string => {
    if (!errorMessage.includes(errorType)) {
        errorMessage = errorMessage.concat(', ', errorType);
    }
    return errorMessage;
};

export enum EErrorBreadcrumb {
    None,
    Macra1_Material,
    Macra2_Hydraulic,
    Macra2_Static = 4,
    Macra2_Basin = 8,
}

interface IErrorBreadcrumb {
    errorMessage: string;
    type: EErrorBreadcrumb;
}

export const addIdToFile = (filename: string) => {
    const id = Math.random().toString(36).substring(2, 10);

    const filenameParts = filename.split('.');
    const extension = filenameParts.length > 1 ? filenameParts.pop() : '';

    const newFilename = `${filenameParts.join('.')}_${id}.${extension}`;

    return newFilename;
};

export const roundNumbers = (obj: any) => {
    const roundNumber = (value: any) => {
        if (typeof value === 'number') {
            return Math.round(value * 1000) / 1000;
        } else {
            return value;
        }
    };
    const roundObject = (obj: any) => {
        let _obj = JSON.parse(JSON.stringify(obj));
        for (let key in _obj) {
            if (_obj.hasOwnProperty(key)) {
                if (typeof _obj[key] === 'object') {
                    _obj[key] = roundObject(_obj[key]);
                } else {
                    _obj[key] = key.toLowerCase() === 'id' ? _obj[key] : roundNumber(_obj[key]);
                }
            }
        }
        return _obj;
    };
    return obj ? roundObject(obj) : undefined;
};
